import { KsService, useApiClient } from '@siclop/kstone-vue'
import { FileEntityType, type ScanStatus, type FileScanResponse } from '@siclop/ksged-vue'
import { ensureSuccess } from '@/shared/utils/apiResult'

/**
 * Service for employee management (CRUD + profile pictures)
 * User-link operations (getUser, createUser) use the useUserLink composable from @siclop/ksadmin-vue
 */
export class EmployeeService extends KsService {
  protected static mainUrl = '/employees'

  /**
   * Upload a profile picture for an employee
   * Returns immediately with status 'scanning' for Minio storage (use SignalR to get final result)
   * Returns with status 'clean' and URL for local storage (immediate result)
   */
  static async uploadProfilePicture(employeeId: number, file: File): Promise<FileScanResponse> {
    const client = useApiClient()
    const axiosInstance = client.getAxiosInstance()
    const formData = new FormData()
    formData.append('file', file)
    const response = await axiosInstance.post<{
      success: boolean
      result?: {
        fileId: number
        entityType: number
        entityId: number
        url?: string | null
        status: string
        message?: string
      }
      message?: string
    }>(
      `${this.mainUrl}/${employeeId}/profile-picture`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    if (response.data.success && response.data.result) {
      return {
        fileId: response.data.result.fileId,
        entityType: response.data.result.entityType as FileEntityType,
        entityId: response.data.result.entityId,
        url: response.data.result.url,
        status: response.data.result.status as ScanStatus,
        message: response.data.result.message
      }
    }
    throw new Error(response.data.message || 'Failed to upload profile picture')
  }

  /**
   * Delete the profile picture of an employee
   */
  static async deleteProfilePicture(employeeId: number): Promise<void> {
    const client = useApiClient()
    // `delete` ne lève pas : l'API répond un DataOperationResult, vérifié ici
    const result = await client.delete(`${this.mainUrl}/${employeeId}/profile-picture`)
    ensureSuccess(result, 'Erreur lors de la suppression de la photo de profil')
  }

  /**
   * Get the profile picture URL and status of an employee
   */
  static async getProfilePicture(employeeId: number): Promise<FileScanResponse> {
    const client = useApiClient()
    try {
      const response = await client.get<{
        success: boolean
        result?: {
          fileId?: number
          entityType?: number
          entityId?: number
          url?: string | null
          status: string
        }
      }>(`${this.mainUrl}/${employeeId}/profile-picture`)
      if (response.success && response.result) {
        return {
          fileId: response.result.fileId,
          entityType: response.result.entityType as FileEntityType,
          entityId: response.result.entityId,
          url: response.result.url,
          status: (response.result.status || 'none') as ScanStatus
        }
      }
      return { status: 'none' }
    } catch {
      return { status: 'none' }
    }
  }

  /**
   * Resend validation email to an employee's user
   */
  static async resendValidationEmail(userId: number): Promise<void> {
    const client = useApiClient()
    const result = await client.postRaw(`/resend-validation-email/${userId}`, {})
    ensureSuccess(result, 'Erreur lors de l\'envoi de l\'email de validation')
  }
}
